import React, {
  memo,
  createContext,
  useRef,
  useEffect,
  useState,
  useCallback,
  useContext,
} from 'react';
import { Navigation } from './components';
export const ModalProviderContext = createContext(null);

import ModalConfirm from './ModalConfirmation';
import ModalScreen from './ModalScreen';

import { getRoutes } from './routes';
import { optionsFromStatics } from './screen_utils';

const routes = getRoutes();

function _mergeOptions(optionsA, optionsB) {
  return {
    ...optionsA,
    ...optionsB,
    topBar: {
      ...optionsA?.topBar,
      ...optionsB?.topBar,
    },
  };
}

function componentOptions(component, modalOptions, passProps) {
  let optionsObj = {};
  let staticOptions = optionsFromStatics(component);

  if (typeof staticOptions === 'function') {
    optionsObj = staticOptions(passProps);
  } else {
    optionsObj = {
      ...staticOptions,
    };
  }

  optionsObj = optionsObj || {};
  // now merge the options with any provided overrides
  return _mergeOptions(optionsObj, modalOptions);
}

const ProviderRegister = memo((props) => {
  const modalProvider = useContext(ModalProviderContext);
  useEffect(() => {
    Navigation.setModalProviderContext(modalProvider);
  }, []);
});

const ModalProvider = memo((props) => {
  const modalConfirmRef = useRef(null);
  const [modalStack, setModalStack] = useState([]);

  useEffect(() => {
    setModalStack([]);
  }, [props.appCompId]);

  useEffect(() => {
    if (!modalStack.length) {
      window.onbeforeunload = null;
    } else {
      window.onbeforeunload = function () {
        return 'You are about the leave the form, your input data will be lost. Are you sure you want to continue?';
      };
    }
  }, [modalStack]);

  const openModal = useCallback((screenConfig, options, passProps, asModal) => {
    asModal = asModal ?? true;
    const ScreenComponent = routes[screenConfig.screen];
    options = componentOptions(ScreenComponent, options, passProps);
    setModalStack((prev) => {
      if (prev.length) {
        options.width = prev[prev.length - 1].options.width;
        options.height = prev[prev.length - 1].options.height;
      }
      return [...prev, { ScreenComponent, options, passProps, asModal }];
    });
  }, []);

  const push = useCallback((screenConfig, options, passProps) => {
    openModal(screenConfig, options, passProps, false);
  }, []);

  const pop = useCallback(() => {
    // return closeAll();
    setModalStack((prev) => [
      ...prev.slice(0, -1),
      prev[prev.length - 1] && { ...prev[prev.length - 1], closing: true },
    ]);
    setTimeout(() => {
      setModalStack((prev) => prev.slice(0, -1));
    }, 300);
  }, []);

  const closeAll = useCallback(({ animated = true } = {}) => {
    if (!animated) {
      setModalStack([]);
      return;
    }
    setModalStack((prev) => prev.map((modal) => ({ ...modal, closing: true, closingAll: true })));
    setTimeout(() => {
      setModalStack([]);
    }, 300);
  }, []);

  const showConfirm = (confirmationOpts) => {
    modalConfirmRef.current?.showConfirm(confirmationOpts);
  };

  const mergeOptions = useCallback((options, componentId) => {
    setModalStack((prev) => {
      return prev.map((modal) => {
        if (modal.passProps?.componentId !== componentId) {
          return modal;
        }
        return {
          ...modal,
          options: _mergeOptions(modal.options, options),
        };
      });
    });
  }, []);

  function renderModal(modalProps, index) {
    return (
      <ModalScreen
        key={modalProps.passProps?.componentId}
        ScreenComponent={modalProps.ScreenComponent}
        options={modalProps.options}
        passProps={modalProps.passProps}
        asModal={modalProps.asModal}
        closingAll={modalProps.closingAll}
        closing={modalProps.closing}
        isTop={index === modalStack.length - 1}
        isBottom={index === 0}
      />
    );
  }
  return (
    <ModalProviderContext.Provider
      value={{
        openModal,
        closeAll,
        push,
        pop,
        mergeOptions,
        showConfirm,
      }}
    >
      <ProviderRegister />
      {props.children}
      <>{modalStack.map(renderModal)}</>
      <ModalConfirm ref={modalConfirmRef} appCompId={props.appCompId} />
    </ModalProviderContext.Provider>
  );
});

export default ModalProvider;
